import { z } from "zod";

import { ScreenType } from "../canvas/supportedScreens.ts";

// TODO: Move to requests.ts and align with Classification.operation
export const Operation = z.enum(["generate", "regenerate", "expand", "edit"]);
export type Operation = z.infer<typeof Operation>;

export const OperationVariant = z.enum([
  "next_screen",
  "error_state",
  "empty_state",
  "web_version",
  "mobile_version",
  "tablet_version",
  "mobile_ios_version",
  "mobile_android_version",
]);
export type OperationVariant = z.infer<typeof OperationVariant>;

export const ExpandVariantMessages: Record<OperationVariant, string> = {
  [OperationVariant.enum.next_screen]: "Create next screen",
  [OperationVariant.enum.error_state]: "Create error state",
  [OperationVariant.enum.empty_state]: "Create empty state",
  [OperationVariant.enum.web_version]: "Create web version",
  [OperationVariant.enum.mobile_version]: "Create mobile version",
  [OperationVariant.enum.tablet_version]: "Create tablet version",
  [OperationVariant.enum.mobile_ios_version]: "Convert to iOS",
  [OperationVariant.enum.mobile_android_version]: "Convert to Android",
};

export const RegenerateVariant = z.enum([
  "different_layout",
  "different_style",
  "from_scratch",
]);
export type RegenerateVariant = z.infer<typeof RegenerateVariant>;

export const RegenerateVariantMessages: Record<RegenerateVariant, string> = {
  [RegenerateVariant.enum.different_layout]: "Try a different layout",
  [RegenerateVariant.enum.different_style]: "Try a different visual style",
  [RegenerateVariant.enum.from_scratch]: "Try again from scratch",
};

export const EditVariant = z.enum(["dark_theme", "light_theme", "make_pretty"]);
export type EditVariant = z.infer<typeof EditVariant>;

export const EditVariantMessages: Record<EditVariant, string> = {
  [EditVariant.enum.dark_theme]: "Switch to dark theme",
  [EditVariant.enum.light_theme]: "Switch to light theme",
  [EditVariant.enum.make_pretty]: "Make it pretty",
};

export const OperationVariantPlatformOverride: Partial<
  Record<OperationVariant, ScreenType>
> = {
  [OperationVariant.enum.web_version]: "web",
  [OperationVariant.enum.mobile_version]: "iphone-vertical",
  [OperationVariant.enum.tablet_version]: "ipad-tablet-vertical",
  [OperationVariant.enum.mobile_ios_version]: "iphone-vertical",
  [OperationVariant.enum.mobile_android_version]: "android-vertical",
};

export const ExpandVariantInstructions: Record<OperationVariant, string> = {
  [OperationVariant.enum.next_screen]:
    "Imagine the next screen in the user flow, as if the user tapped an element. Reuse structure, navigation, content, and theme where applicable.",
  [OperationVariant.enum.error_state]:
    "Imagine the most plausible error for this context (e.g. invalid input, insufficient funds, lost connection). Reuse structure, navigation, content, and theme.",
  [OperationVariant.enum.empty_state]:
    "Imagine this screen with no content (e.g. empty playlist, empty cart, empty contact list). Reuse structure, navigation, and theme.",
  [OperationVariant.enum.web_version]:
    "Adapt this screen to a desktop web layout. Different navigation patterns and structure are expected. Reuse content and theme.",
  [OperationVariant.enum.mobile_version]:
    "Adapt this screen to a mobile layout. Different navigation patterns and structure are expected. Reuse content and theme.",
  [OperationVariant.enum.tablet_version]:
    "Adapt this screen to a tablet layout. Take advantage of the larger canvas — expand the content area, consider split-pane or sidebar patterns where appropriate. Reuse content and theme.",
  [OperationVariant.enum.mobile_ios_version]:
    "Convert this Android screen to iOS. Adapt navigation patterns (e.g. bottom tab bar, back gestures), system elements, and UI components to follow iOS Human Interface Guidelines. Reuse content and colour theme.",
  [OperationVariant.enum.mobile_android_version]:
    "Convert this iOS screen to Android. Adapt navigation patterns (e.g. material top bar, back arrow), system elements, and UI components to follow Material Design guidelines. Reuse content and colour theme.",
};

const AXIS_LAYOUT =
  "the main content layout (structure, hero treatment, item arrangement, section order, spacing, alignment)";
const AXIS_VISUAL_STYLE =
  "the visual style (colors, typography, elevation & depth, shapes)";
const AXIS_CONTENT = "images, copy, and section count/grouping";

const REGENERATE_MUST_DIFFER =
  "The previous screen is a reference to diverge from — the output must clearly differ on the axes you are asked to change; do not imitate or lightly tweak it.";
const REGENERATE_CHANGE_LAYOUT = `Change ${AXIS_LAYOUT}.`;
const REGENERATE_CHANGE_VISUAL_STYLE = `Change ${AXIS_VISUAL_STYLE}.`;
const REGENERATE_CHANGE_CONTENT = `Change ${AXIS_CONTENT}.`;
const REGENERATE_KEEP_PURPOSE =
  "Preserve the previous screen's purpose/functionality and any explicit user requirements.";
const EDIT_REFINE_VISUAL_QUALITY = `Refine ${AXIS_VISUAL_STYLE}, spacing, and alignment to improve visual hierarchy, balance, and contrast/accessibility. Restyle and reposition only what is already on the screen: do not add, remove, or duplicate any element. Give the screen one focal point, and keep the accent on actions rather than spreading it across unrelated elements; selected and primary controls must outweigh their siblings. Preserve colors that carry meaning — ratings, status, categories, brand — instead of flattening them into the accent or a neutral. Collapse each property (spacing, type sizes and weights, radii, shadows, neutrals) onto one consistent scale, and keep repeated components (buttons, cards, inputs) visually consistent with each other. Separate groups more than the items inside them. Work within the screen's existing design language — make it more itself, not more generic.`;

export const RegenerateVariantInstructions: Record<RegenerateVariant, string> =
  {
    [RegenerateVariant.enum.different_layout]: [
      "Retry with a different layout for the main content area.",
      REGENERATE_MUST_DIFFER,
      REGENERATE_CHANGE_LAYOUT,
      "Keep visual style, content, components, and navigation unchanged.",
      REGENERATE_KEEP_PURPOSE,
    ].join(" "),
    [RegenerateVariant.enum.different_style]: [
      "Retry with a different visual style.",
      REGENERATE_MUST_DIFFER,
      REGENERATE_CHANGE_VISUAL_STYLE,
      "Keep layout, content, components, and navigation unchanged.",
      REGENERATE_KEEP_PURPOSE,
    ].join(" "),
    [RegenerateVariant.enum.from_scratch]: [
      "Retry the original prompt with a completely different approach.",
      REGENERATE_MUST_DIFFER,
      REGENERATE_CHANGE_LAYOUT,
      REGENERATE_CHANGE_VISUAL_STYLE,
      REGENERATE_CHANGE_CONTENT,
      "Keep navigation unchanged.",
      REGENERATE_KEEP_PURPOSE,
    ].join(" "),
  };

export const EditPresetInstructions: Record<EditVariant, string> = {
  [EditVariant.enum.dark_theme]:
    "Switch this screen to dark mode. Evaluate contrast ratios and fix any accessibility inconsistencies.",
  [EditVariant.enum.light_theme]:
    "Switch this screen to light mode. Evaluate contrast ratios and fix any accessibility inconsistencies.",
  [EditVariant.enum.make_pretty]: [
    "Polish this screen for visual quality and appeal by applying UI/UX design principles.",
    EDIT_REFINE_VISUAL_QUALITY,
    "Keep the structure, hero treatment, item arrangement, section order, content, components, and navigation unchanged.",
  ].join(" "),
};
